import styled, { css, FlattenSimpleInterpolation, keyframes, Keyframes } from 'styled-components';

import { ButtonTheme, Color, FontWeight } from '../../enums';
import { StyledComponent } from '../../interfaces';

import { BaseButtonProps, IconContainerProps, LabelContainerProps, LoadingDotProps } from './button.types';

const flashingAnimation: (color: Color) => Keyframes = (color: Color): Keyframes => keyframes`
  0% { 
    background-color: ${color};
  }
  50% { 
    background-color: ${color};
    opacity: 0.5;
  }
  100% { 
    background-color: ${color};
    opacity: 0.2;
  }
`;

const primaryButtonsCommonStyle: FlattenSimpleInterpolation = css`
  background-color: ${Color.Black};
  color: ${Color.White};
  border: 1px solid ${Color.Black};

  &:hover {
    background-color: ${Color.Error500};
    border: 1px solid ${Color.Error500};
  }

  &:active {
    background-color: ${Color.Gray500};
    border: 1px solid ${Color.Gray500};
  }

  &:disabled {
    background-color: ${Color.Gray300};
    color: ${Color.Gray400};
    border: 1px solid ${Color.Transparent};
  }
`;

const secondaryTransparentButtonsCommonStyle: FlattenSimpleInterpolation = css`
  background-color: ${Color.Transparent};
  color: ${Color.White};
  border: 1px solid ${Color.White};

  &:hover {
    background-color: ${Color.Error500};
    border: 1px solid ${Color.Error500};
  }

  &:active {
    background-color: ${Color.White};
    border: 1px solid ${Color.White};
    color: ${Color.Gray400};
  }

  &:disabled {
    background-color: ${Color.Gray300};
    color: ${Color.Gray400};
    border: 1px solid ${Color.Transparent};
  }
`;

const secondarySolidButtonsCommonStyle: FlattenSimpleInterpolation = css`
  background-color: ${Color.Transparent};
  color: ${Color.Black};
  border: 1px solid ${Color.Black};

  &:hover {
    background-color: ${Color.Error500};
    border: 1px solid ${Color.Error500};
    color: ${Color.White};
  }

  &:active {
    background-color: ${Color.Transparent};
    border: 1px solid ${Color.Gray500};
    color: ${Color.Black};
  }

  &:disabled {
    background-color: ${Color.Gray300};
    color: ${Color.Gray400};
    border: 1px solid ${Color.Transparent};
  }
`;

const ButtonStyles: { [b in ButtonTheme]: FlattenSimpleInterpolation } = {
  [ButtonTheme.GrayTransparent]: css`
    background-color: ${Color.Transparent};
    color: ${Color.Gray500};
    font-weight: ${FontWeight.Regular};
    padding: 0 10px;
    min-width: 155px;
    font-size: 14px;
    line-height: 18px;
  `,
  [ButtonTheme.PrimarySmall]: css`
    ${primaryButtonsCommonStyle}
    padding: 14px 28px;
    font-size: 14px;
    line-height: 18px;
  `,
  [ButtonTheme.PrimaryBig]: css`
    ${primaryButtonsCommonStyle}
    padding: 17px 48px;
  `,
  [ButtonTheme.SecondaryTransparentSmall]: css`
    ${secondaryTransparentButtonsCommonStyle}
    padding: 14px 28px;
    font-size: 14px;
    line-height: 18px;
  `,
  [ButtonTheme.SecondaryTransparentBig]: css`
    ${secondaryTransparentButtonsCommonStyle}
    padding: 17px 48px;
  `,
  [ButtonTheme.SecondarySolidSmall]: css`
    ${secondarySolidButtonsCommonStyle}
    padding: 14px 28px;
    font-size: 14px;
    line-height: 18px;
  `,
  [ButtonTheme.SecondarySolidBig]: css`
    ${secondarySolidButtonsCommonStyle}
    padding: 17px 48px;
  `,
};

export const BaseButton: StyledComponent<'button', BaseButtonProps> = styled.button<BaseButtonProps>`
  font-weight: ${FontWeight.Medium};
  font-size: 16px;
  line-height: 22px;
  text-align: center;
  transition: 0.3s;
  ${({ $theme }: BaseButtonProps): FlattenSimpleInterpolation => ButtonStyles[$theme]};
  ${({ $fullWidth }: BaseButtonProps): string | false => !!$fullWidth && 'width: 100%'};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

export const LabelContainer: StyledComponent<'div', LabelContainerProps> = styled.div<LabelContainerProps>`
  visibility: ${({ $visible }: LabelContainerProps): string => $visible ? 'visible' : 'hidden'};
`;

export const IconContainer: StyledComponent<'div', IconContainerProps> = styled.div<IconContainerProps>`
  margin-right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  visibility: ${({ $visible }: IconContainerProps): string => $visible ? 'visible' : 'hidden'};
`;

export const LoadingContainer: StyledComponent<'div'> = styled.div`
  display: flex;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

export const LoadingDot: StyledComponent<'div', LoadingDotProps> = styled.div<LoadingDotProps>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ $color }: LoadingDotProps): string => $color};
  animation: ${({ $color }: LoadingDotProps): FlattenSimpleInterpolation => css`
    ${flashingAnimation($color)} .7s infinite alternate
  `};
  animation-delay: .5s;
  transition: .3s;

  & + & {
    margin-left: 8px;
  }

  :first-child {
    animation-delay: 0s;
  }

  :last-child {
    animation-delay: 1s;
  }
`;
